/**
 * Speech-to-text service factory.
 *
 * STT is independent of AI Coach — consumers only receive text.
 * Future: CloudSpeechService behind the same callbacks when local is unsupported.
 */

import {
  checkBrowserCapability,
  hasWebSpeechRecognition,
  isMobileVoiceClient,
} from "./BrowserCapability";
import { BrowserSpeechService } from "./BrowserSpeechService";
import { WhisperWasmService } from "./WhisperWasmService";
import { DEFAULT_MODEL_ID } from "./ModelManager";
import { VoiceState } from "./voiceState";

/**
 * @typedef {object} SpeechToTextService
 * @property {() => Promise<void>} initialize
 * @property {(opts?: { languageHint?: string }) => Promise<void>} startListening
 * @property {() => Promise<void>} stopListening
 * @property {(cb: Function) => void} onPartial
 * @property {(cb: Function) => void} onFinal
 * @property {(cb: Function) => void} onError
 * @property {(cb: Function) => void} onProgress
 * @property {(cb: Function) => void} onState
 * @property {(cb: Function) => void} onLevel
 * @property {() => string} getState
 * @property {() => void} dispose
 * @property {object|null} lastMetrics
 */

/**
 * @param {{ modelId?: string, preferLocal?: boolean, engine?: "web-speech"|"whisper" }} [options]
 * @returns {SpeechToTextService}
 */
export function createSpeechToTextService(options = {}) {
  const preferLocal = options.preferLocal !== false;
  const cap = checkBrowserCapability();

  if (!preferLocal || !cap.ok) {
    return createUnsupportedService(cap.reason || VoiceState.UNSUPPORTED_BROWSER);
  }

  const forceWhisper = options.engine === "whisper" || Boolean(options.modelId);
  if (!forceWhisper && hasWebSpeechRecognition()) {
    return new BrowserSpeechService();
  }

  // Mobile Whisper/WASM often hangs (ORT Cache.put, no WebGPU). Don't start it.
  if (!forceWhisper && isMobileVoiceClient()) {
    return createUnsupportedService(VoiceState.UNSUPPORTED_BROWSER);
  }

  return new WhisperWasmService({
    modelId: options.modelId || DEFAULT_MODEL_ID,
  });
}

export { VoiceState, WhisperWasmService, checkBrowserCapability, DEFAULT_MODEL_ID };

function createUnsupportedService(reason) {
  let onError = null;
  let onState = null;
  return {
    lastMetrics: null,
    async initialize() {
      if (onState) onState(reason);
      if (onError) onError({ code: reason, message: "Local STT unavailable" });
      throw new Error(reason);
    },
    async startListening() {
      return this.initialize();
    },
    async stopListening() {},
    onPartial() {},
    onFinal() {},
    onError(cb) {
      onError = cb;
    },
    onProgress() {},
    onState(cb) {
      onState = cb;
    },
    onLevel() {},
    getState() {
      return reason;
    },
    dispose() {},
  };
}
